import React, { useEffect, useMemo, useState } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Save,
  X,
  Info,
  Trash2,
  ArrowLeft,
  RefreshCcw,
  CreditCard,
  Percent,
  Landmark,
  ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * MetodosCobroForm - Enterprise Unificada (Estilo Productos)
 * - Header: Título + Botón de Acción Principal (Guardar)
 * - Lógica de Bancos blindada
 */
function MetodosCobroForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);

  const [bancos, setBancos] = useState([]);
  
  // Estado del formulario inicializado correctamente para evitar errores de "controlled input"
  const [form, setForm] = useState({
    name: '',
    discount_percentage: 0,
    surcharge_percentage: 0,
    commission_percentage: 0,
    accreditation_days: 0,
    accreditation_business_days: false,
    bank_id: '', // Siempre string vacío al inicio, nunca null/undefined
    gateway_type: 'MANUAL',
    getnet_mode: 'QR', // Modo Getnet: 'QR' presencial o 'TERMINAL' SmartPOS
    getnet_pos_id: '', // Point of sale de Geopagos para crear el intent
    // Mercado Pago: 'QR' (instore, usa sitios.mp_pos_id) o 'POINT' (terminal
    // posnet, usa sitios.mp_terminal_id). El tipo y las cuotas viven acá y no en
    // la venta porque la acreditación y la comisión se derivan del método: por
    // eso débito y crédito tienen que ser métodos distintos.
    mp_mode: 'QR',
    mp_default_type: 'credit_card',
    mp_default_installments: 1,
    mp_print_on_terminal: false
  });

  const goBack = () => navigate('/sistema/metodos-cobro');

  useEffect(() => {
    if (!profile?.organization_id) return;

    (async () => {
      setLoadingInit(true);
      try {
        await fetchBancos();
        if (id) await fetchMetodo();
      } finally {
        setLoadingInit(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile?.organization_id]);

  async function fetchBancos() {
    const { data, error } = await supabase
      .from('banks')
      .select('id, name')
      .eq('organization_id', profile.organization_id)
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error(error);
      toast.error('Error cargando bancos');
      setBancos([]);
      return;
    }
    setBancos(data || []);
  }

  async function fetchMetodo() {
    const { data, error } = await supabase
      .from('payment_methods')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(error);
      toast.error('No se pudo cargar el método');
      return;
    }

    if (data) {
      setForm({
        name: data.name || '',
        discount_percentage: data.discount_percentage ?? 0,
        surcharge_percentage: data.surcharge_percentage ?? 0,
        commission_percentage: data.commission_percentage ?? 0,
        accreditation_days: data.accreditation_days ?? 0,
        accreditation_business_days: Boolean(data.accreditation_business_days),
        // CRÍTICO: Si viene null de la DB, lo convertimos a '' para el Select
        bank_id: data.bank_id || '',
        gateway_type: data.gateway_type || 'MANUAL',
        getnet_mode: data.getnet_mode || 'QR',
        getnet_pos_id: data.getnet_pos_id || '',
        // mp_mode ausente = método creado antes de Point: se trata como QR.
        mp_mode: data.mp_mode || 'QR',
        mp_default_type: data.mp_default_type || 'credit_card',
        mp_default_installments: Number(data.mp_default_installments) || 1,
        mp_print_on_terminal: Boolean(data.mp_print_on_terminal)
});
    }
  }

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  
  const toNum = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  const normalized = useMemo(() => {
    const name = (form.name || '').trim();

    // Normalización numérica segura
    const discount = clamp(toNum(form.discount_percentage), 0, 100);
    const surcharge = clamp(toNum(form.surcharge_percentage), 0, 100);
    const commission = clamp(toNum(form.commission_percentage), 0, 100);
    const accreditationDays = Math.max(0, Math.trunc(toNum(form.accreditation_days)));

    return {
      name,
      discount,
      surcharge,
      commission,
      accreditation_days: accreditationDays,
      accreditation_business_days: Boolean(form.accreditation_business_days),
      bank_id: form.bank_id || '',
      gateway_type: form.gateway_type || 'MANUAL',
      getnet_mode: form.getnet_mode || 'QR',
      getnet_pos_id: (form.getnet_pos_id || '').trim(),
      mp_mode: form.mp_mode || 'QR',
      mp_default_type: form.mp_default_type || 'credit_card',
      mp_default_installments: Math.min(24, Math.max(1, Number(form.mp_default_installments) || 1)),
      mp_print_on_terminal: Boolean(form.mp_print_on_terminal)
    };
  }, [form]);

  const canSave = useMemo(() => normalized.name.length >= 2, [normalized.name]);

  const handleSave = async (e) => {
    e.preventDefault(); // Previene recarga si se dispara por submit
    if (!profile?.organization_id) return toast.error('Sesión inválida');
    if (!canSave) return toast.error('Completá un nombre válido');

    setLoading(true);

    const payload = {
      name: normalized.name,
      discount_percentage: normalized.discount,
      surcharge_percentage: normalized.surcharge,
      commission_percentage: normalized.commission,
      accreditation_days: normalized.accreditation_days,
      accreditation_business_days: normalized.accreditation_business_days,
      // CRÍTICO: Si es string vacío, mandamos NULL a la base de datos
      bank_id: normalized.bank_id ? normalized.bank_id : null,
      gateway_type: normalized.gateway_type,
      // Solo persistimos config Getnet si la pasarela es GETNET
      getnet_mode: normalized.gateway_type === 'GETNET' ? normalized.getnet_mode : null,
      getnet_pos_id: normalized.gateway_type === 'GETNET' ? (normalized.getnet_pos_id || null) : null,
      // Solo persistimos config MP si la pasarela es MERCADOPAGO
      mp_mode: normalized.gateway_type === 'MERCADOPAGO' ? normalized.mp_mode : null,
      mp_default_type: (normalized.gateway_type === 'MERCADOPAGO' && normalized.mp_mode === 'POINT')
        ? normalized.mp_default_type : null,
      mp_default_installments: (normalized.gateway_type === 'MERCADOPAGO' && normalized.mp_mode === 'POINT')
        ? normalized.mp_default_installments : null,
      mp_print_on_terminal: (normalized.gateway_type === 'MERCADOPAGO' && normalized.mp_mode === 'POINT')
        ? normalized.mp_print_on_terminal : false,
      organization_id: profile.organization_id,
      is_active: true
    };

    const persist = (p) => id
      ? supabase.from('payment_methods').update(p).eq('id', id)
      : supabase.from('payment_methods').insert([p]);

    try {
      let { error } = await persist(payload);

      // Defensivo: si las columnas getnet_* aún no existen (migración pendiente),
      // reintentamos sin ellas para no bloquear el guardado del método.
      if (error && error.code === 'PGRST204' && /getnet_mode|getnet_pos_id/i.test(error.message || '')) {
        console.warn('[MetodosCobroForm] Columnas getnet_* no existen; reintentando sin ellas.');
        toast('Tip: aplicá la migración SQL de Getnet para guardar el modo de cobro.', { icon: '⚠️', duration: 6000 });
        const { getnet_mode, getnet_pos_id, ...rest } = payload;
        ({ error } = await persist(rest));
      }

      // Mismo criterio para las columnas mp_* (migración 20260903180000_mp_point).
      if (error && error.code === 'PGRST204' && /mp_mode|mp_default_type|mp_default_installments|mp_print_on_terminal/i.test(error.message || '')) {
        console.warn('[MetodosCobroForm] Columnas mp_* no existen; reintentando sin ellas.');
        toast('Tip: aplicá la migración SQL de Mercado Pago Point para guardar el modo de cobro.', { icon: '⚠️', duration: 6000 });
        const { mp_mode, mp_default_type, mp_default_installments, mp_print_on_terminal, ...rest } = payload;
        ({ error } = await persist(rest));
      }

      if (error) throw error;

      toast.success(id ? 'MÉTODO ACTUALIZADO' : 'MÉTODO CREADO');
      navigate('/sistema/metodos-cobro');
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar el método');
    } finally {
      setLoading(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!id) return;
    const ok = window.confirm(
      '¿Confirmar baja técnica? No se mostrará en el POS pero se mantendrá en registros históricos.'
    );
    if (!ok) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('payment_methods')
        .update({ is_active: false, archived_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast.success('MÉTODO DESACTIVADO');
      navigate('/sistema/metodos-cobro');
    } catch (err) {
      console.error(err);
      toast.error('Error al dar de baja');
    } finally {
      setLoading(false);
    }
  };

  if (loadingInit) {
    return (
      <div className="w-full p-4 md:p-6 lg:p-8 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 shadow-sm" />
            <div className="space-y-2">
              <div className="h-4 w-56 bg-slate-200 rounded" />
              <div className="h-3 w-72 bg-slate-100 rounded" />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3">
              <RefreshCcw className="animate-spin text-[#51A7FD]" size={18} />
              <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
                Sincronizando configuración...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 md:p-6 lg:p-8 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      <div className="max-w-4xl mx-auto">

        {/* --- HEADER ESTILO PRODUCTOS --- */}
        {/* Título a la izquierda, Botón de Acción Principal a la derecha */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 px-1">
          <div className="flex items-start gap-4">
            <button
              onClick={goBack}
              className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] transition-all shadow-sm"
              title="Volver al listado"
            >
              <ArrowLeft size={20} />
            </button>

            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-800 uppercase tracking-tight">
                {id ? 'Editar Método de Cobro' : 'Nuevo Método de Cobro'}
              </h1>
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mt-1 italic">
                {id ? `Editando: ${form.name}` : 'Creación de nueva pasarela o medio de pago'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:justify-end">
            <button
              onClick={goBack}
              className="px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-rose-500 font-bold text-[10px] uppercase tracking-widest shadow-sm transition-colors"
            >
              Cancelar
            </button>
            
            {/* BOTÓN PRINCIPAL EN EL HEADER */}
            <button
              data-onboarding-id="payment-method-submit"
              onClick={handleSave}
              disabled={loading || !canSave}
              className="px-6 py-3 rounded-xl bg-[#3FF2AA] text-[#0B2A36] font-bold text-[10px] uppercase tracking-widest shadow-lg hover:brightness-95 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:translate-y-0 transition-all flex items-center gap-2"
            >
              {loading ? <RefreshCcw className="animate-spin" size={16} /> : <Save size={16} />}
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>

        {/* FORMULARIO */}
        <form onSubmit={handleSave} className="space-y-4 animate-nova-in">
          
          {/* CARD PRINCIPAL */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Header de la Card */}
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 text-[#51A7FD] rounded-xl flex items-center justify-center border border-blue-100">
                  <CreditCard size={18} />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-widest text-slate-700">
                    Identificación y reglas
                  </div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Configuración financiera básica
                  </div>
                </div>
              </div>

              <div className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-400 hidden sm:block">
                {id ? 'MODO EDICIÓN' : 'MODO CREACIÓN'}
              </div>
            </div>

            {/* Body */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-12 gap-6">
              
              {/* Nombre */}
              <div className="md:col-span-12 space-y-2">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Nombre visible en POS <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-bold outline-none focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10 shadow-inner uppercase transition-all"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: EFECTIVO, TRANSFERENCIA, VISA DÉBITO..."
                  required
                />
              </div>

              {/* Sección Tesorería (Prioridad) */}
              <div className="md:col-span-12 pt-1 border-t border-slate-50">
                <div className="flex items-center gap-2 mb-3 mt-1">
                  <Landmark size={16} className="text-[#51A7FD]" />
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Vinculación Bancaria
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                    Cuenta de destino de los fondos
                  </label>
                  <div className="relative">
                    <select
                      data-onboarding-id="payment-method-bank-select"
                      className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-bold outline-none focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10 shadow-inner appearance-none cursor-pointer transition-all"
                      value={form.bank_id}
                      onChange={(e) => setForm({ ...form, bank_id: e.target.value })}
                    >
                      <option value="">— SIN VINCULACIÓN (CONTROL EN CAJA) —</option>
                      {bancos.map((b) => (
                        <option key={b.id} value={b.id}>
                          {String(b.name || '').toUpperCase()}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
                      size={16}
                    />
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                    Tipo de Integración (Pasarela Automática)
                  </label>
                  <div className="relative">
                    <select
                      className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-bold outline-none focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10 shadow-inner appearance-none cursor-pointer transition-all"
                      value={form.gateway_type}
                      onChange={(e) => setForm({ ...form, gateway_type: e.target.value })}
                    >
                      <option value="MANUAL">MANUAL (SE REGISTRA EL PAGO MANUALMENTE)</option>
                      <option value="MERCADOPAGO">MERCADO PAGO (QR O TERMINAL POINT)</option>
                      <option value="GETNET">GETNET (SANTANDER / GEOPAGOS)</option>
                    </select>
                    <ChevronDown
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
                      size={16}
                    />
                  </div>
                </div>

                {/* Configuración específica de Mercado Pago */}
                {form.gateway_type === 'MERCADOPAGO' && (
                  <div className="mt-4 p-4 rounded-2xl border border-sky-100 bg-sky-50/40 space-y-4">
                    <div className="text-[9px] font-black uppercase tracking-widest text-sky-600">
                      Configuración Mercado Pago
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Modo de cobro
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, mp_mode: 'QR' })}
                          className={`px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${form.mp_mode === 'QR' ? 'bg-sky-500 text-white border-sky-500 shadow' : 'bg-white text-slate-500 border-slate-200 hover:border-sky-300'}`}
                        >
                          QR Presencial
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, mp_mode: 'POINT' })}
                          className={`px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${form.mp_mode === 'POINT' ? 'bg-sky-500 text-white border-sky-500 shadow' : 'bg-white text-slate-500 border-slate-200 hover:border-sky-300'}`}
                        >
                          Terminal Point
                        </button>
                      </div>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide ml-1">
                        {form.mp_mode === 'POINT'
                          ? 'El cobro se envía al posnet de la sucursal. Cargá la terminal en Configuración → Sucursales.'
                          : 'Se genera un QR que el cliente escanea. Usa el ID de caja QR de la sucursal.'}
                      </p>
                    </div>

                    {form.mp_mode === 'POINT' && (
                      <>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                            Tipo de tarjeta
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, mp_default_type: 'debit_card', mp_default_installments: 1 })}
                              className={`px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${form.mp_default_type === 'debit_card' ? 'bg-sky-500 text-white border-sky-500 shadow' : 'bg-white text-slate-500 border-slate-200 hover:border-sky-300'}`}
                            >
                              Débito
                            </button>
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, mp_default_type: 'credit_card' })}
                              className={`px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${form.mp_default_type === 'credit_card' ? 'bg-sky-500 text-white border-sky-500 shadow' : 'bg-white text-slate-500 border-slate-200 hover:border-sky-300'}`}
                            >
                              Crédito
                            </button>
                          </div>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide ml-1">
                            Débito y crédito acreditan y comisionan distinto: creá un método por tipo
                            para que el flujo de fondos dé bien.
                          </p>
                        </div>

                        {form.mp_default_type === 'credit_card' && (
                          <div className="space-y-2">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                              Cuotas por defecto
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="24"
                              className="w-full bg-white border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-bold outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 shadow-inner transition-all"
                              value={form.mp_default_installments}
                              onChange={(e) => setForm({ ...form, mp_default_installments: e.target.value })}
                            />
                            {Number(form.mp_default_installments) > 1 && !Number(form.surcharge_percentage) && (
                              <p className="text-[9px] font-black text-amber-600 uppercase tracking-wide ml-1">
                                ⚠ El costo de las cuotas lo absorbe el comercio. Si querés trasladarlo al
                                cliente, cargá el recargo del método más arriba.
                              </p>
                            )}
                          </div>
                        )}

                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(form.mp_print_on_terminal)}
                            onChange={(e) => setForm({ ...form, mp_print_on_terminal: e.target.checked })}
                            className="w-4 h-4 rounded accent-sky-500"
                          />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            Imprimir cupón en la terminal
                          </span>
                        </label>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide ml-1 -mt-2">
                          Si está apagado, solo imprime el ticket de Impulza.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Configuración específica de Getnet */}
                {form.gateway_type === 'GETNET' && (
                  <div className="mt-4 p-4 rounded-2xl border border-rose-100 bg-rose-50/40 space-y-4">
                    <div className="text-[9px] font-black uppercase tracking-widest text-rose-500">
                      Configuración Getnet
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Modo de cobro
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, getnet_mode: 'QR' })}
                          className={`px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${form.getnet_mode === 'QR' ? 'bg-rose-500 text-white border-rose-500 shadow' : 'bg-white text-slate-500 border-slate-200 hover:border-rose-300'}`}
                        >
                          QR Presencial
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, getnet_mode: 'TERMINAL' })}
                          className={`px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${form.getnet_mode === 'TERMINAL' ? 'bg-rose-500 text-white border-rose-500 shadow' : 'bg-white text-slate-500 border-slate-200 hover:border-rose-300'}`}
                        >
                          Terminal SmartPOS
                        </button>
                      </div>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide ml-1">
                        {form.getnet_mode === 'TERMINAL'
                          ? 'El cobro se envía a la terminal física asociada al punto de venta.'
                          : 'Se genera un QR que el cliente escanea con su celular.'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Point of Sale ID (Geopagos) <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full bg-white border border-slate-200 px-4 py-3 rounded-2xl text-[11px] font-bold outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-400/10 shadow-inner font-mono transition-all"
                        value={form.getnet_pos_id}
                        onChange={(e) => setForm({ ...form, getnet_pos_id: e.target.value })}
                        placeholder="ID del punto de venta provisto por Getnet"
                      />
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide ml-1">
                        Identificador del punto de venta de Geopagos al que se asocia la transacción (necesario para QR y Terminal en el POS).
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                      Dias hasta acreditar
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10 shadow-inner text-right transition-all"
                      data-onboarding-id="payment-method-accreditation-input"
                      value={form.accreditation_days}
                      onChange={(e) => setForm({ ...form, accreditation_days: e.target.value })}
                    />
                  </div>

                  <label className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-slate-100 bg-slate-50 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={Boolean(form.accreditation_business_days)}
                      onChange={(e) => setForm({ ...form, accreditation_business_days: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-[#0B2A36] focus:ring-[#3FF2AA]"
                    />
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                        Contar solo dias habiles
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400">
                        Lunes a viernes
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Ajustes Numéricos */}
              <div className="md:col-span-12 pt-1 border-t border-slate-50">
                <div className="flex items-center gap-2 mb-3 mt-1">
                  <Percent size={16} className="text-[#51A7FD]" />
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Ajustes Automáticos en Venta
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  
                  {/* Descuento */}
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1 flex justify-between">
                      <span>Descuento (%)</span>
                      <span className="text-emerald-500">RESTA</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      className="w-full bg-emerald-50/30 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-black text-emerald-600 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/10 shadow-inner text-right transition-all"
                      value={form.discount_percentage}
                      onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                    />
                  </div>

                  {/* Recargo */}
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1 flex justify-between">
                      <span>Recargo (%)</span>
                      <span className="text-orange-500">SUMA</span>
                    </label>
                    <input
                      data-onboarding-id="payment-method-charge-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      className="w-full bg-orange-50/30 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-black text-orange-600 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/10 shadow-inner text-right transition-all"
                      value={form.surcharge_percentage}
                      onChange={(e) => setForm({ ...form, surcharge_percentage: e.target.value })}
                    />
                  </div>

                  {/* Comisión */}
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1 flex justify-between">
                      <span>Comisión (%)</span>
                      <span className="text-rose-500">COSTO</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className="w-full bg-rose-50/30 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-black text-rose-500 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-400/10 shadow-inner text-right transition-all"
                      value={form.commission_percentage}
                      onChange={(e) => setForm({ ...form, commission_percentage: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="mt-4 bg-blue-50/50 p-4 rounded-2xl border border-blue-100 flex items-start gap-3">
                  <Info size={16} className="text-[#51A7FD] shrink-0 mt-0.5" />
                  <p className="text-[9px] text-blue-800 font-bold uppercase leading-relaxed">
                    Descuentos y Recargos alteran el total a pagar por el cliente. <br/>
                    La Comisión es un costo interno que no ve el cliente, útil para calcular rentabilidad neta.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer actions secundario (opcional, por si el form es muy largo) */}
            <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center justify-between">
               <div className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-300">
                {BRAND_NAME} ERP
              </div>
              
              {id && (
                <button
                  type="button"
                  onClick={handleSoftDelete}
                  disabled={loading}
                  className="px-5 py-3 rounded-2xl bg-white border border-slate-200 text-slate-400 hover:bg-rose-50 hover:border-rose-100 hover:text-rose-500 font-black text-[10px] uppercase tracking-widest shadow-sm disabled:opacity-50 flex items-center gap-2 transition-all"
                >
                  <Trash2 size={16} />
                  Eliminar Método
                </button>
              )}
            </div>
          </div>

          <div className="h-10" />
        </form>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default MetodosCobroForm;
